"use client"
import { getUser } from "@opensea/embedded-wallet-sdk"
import { isAddressEqual } from "@opensea/utils"
import { useCallback, useState } from "react"
import { useSnapshot } from "valtio"
import { useSignature, useSyncAuth } from "../../polychain/hooks"
import { useIsEmbeddedWallet } from "../../polychain/hooks/useIsEmbeddedWallet"
import { AccountActions, AccountState } from "../../stores/accountStore"
import { useSiweAdapter } from "./SiweProvider"

export type SiwePromptParams = {
  address: string
  chainId: number
  connectorId: string
}

// --- MODULE-SCOPED DEDUPE STATE ---
type Key = string
const inFlight = new Map<Key, Promise<unknown>>()
const keyOf = (address: string, chainId: number, connectorId?: string | null) =>
  `${address}:${chainId}:${connectorId ?? ""}`

export function useSiwe() {
  const adapter = useSiweAdapter()
  const isEmbeddedWallet = useIsEmbeddedWallet()
  const { sign } = useSignature()
  const { syncAuth } = useSyncAuth()

  const [isSiwePending, setIsSiwePendingState] = useState(false)

  const {
    address: storeAddress,
    chainId: storeChainId,
    chainArch,
    connectorId: storeConnectorId,
  } = useSnapshot(AccountState)

  const promptSiwe = useCallback(
    async (params?: SiwePromptParams) => {
      const address = params?.address ?? storeAddress
      const chainId = params?.chainId ?? storeChainId
      const connectorId = params?.connectorId ?? storeConnectorId

      if (!adapter) {
        throw new Error(
          "Cannot prompt SIWE without providing a SIWE adapter in WalletProvider props",
        )
      }
      if (!(address && chainId)) {
        throw new Error("Cannot prompt SIWE without a connected account")
      }
      const k = keyOf(address, chainId, connectorId)

      const existing = inFlight.get(k)
      if (existing) {
        return existing
      }

      // Create the one true promise for this key and share it.
      const p = (async () => {
        setIsSiwePendingState(true)
        try {
          // 1) Try refresh
          if (adapter.tryRefresh) {
            const refreshSession = await adapter.tryRefresh()
            if (
              refreshSession?.user &&
              isAddressEqual(refreshSession.user.address, address)
            ) {
              AccountActions.setAuthenticated(true)
              return refreshSession
            }
          }

          // 2) Embedded wallet fast-path via exchange token
          if (isEmbeddedWallet && adapter.exchangeToken) {
            const user = await getUser()
            const newSession = await adapter.exchangeToken({
              token: user.accessToken,
            })
            AccountActions.setAuthenticated(true)
            return newSession
          }

          // 3) Regular SIWE
          const nonce = await adapter.getNonce()
          const message = adapter.createMessage({ nonce, address, chainId })
          const signature = await sign({ address, message })

          const newSession = await adapter.verify({
            message,
            signature,
            chainArch,
            connectorId,
          })

          if (
            newSession.user &&
            isAddressEqual(newSession.user.address, address)
          ) {
            AccountActions.setAuthenticated(true)
            return newSession
          }
          throw new Error("Invalid session")
        } catch (e) {
          AccountActions.setAuthenticated(false)
          throw e
        } finally {
          setIsSiwePendingState(false)
          syncAuth()
        }
      })()

      // Ensure cleanup of the inFlight map.
      inFlight.set(k, p)
      p.finally(() => {
        // only clear if the same promise is still stored
        if (inFlight.get(k) === p) {
          inFlight.delete(k)
        }
      })

      return p
    },
    [
      adapter,
      storeAddress,
      chainArch,
      storeChainId,
      storeConnectorId,
      isEmbeddedWallet,
      sign,
      syncAuth,
    ],
  )

  return { isSiwePending, promptSiwe }
}
